import React, { useEffect, useRef, useState } from 'react';
import { medianInterval, formatRate } from './gatewaySamples';

// Countdown label to the next expected sample, with granularity chosen from the sample rate:
//   - fast rate (< 10s):  tick every second  (in 5s → 4s → 3s …)
//   - 10s–60s rate:       5-second steps      (in 25s → 20s …)
//   - >= 60s rate:        minutes while > 60s left, then 5-second steps under a minute
//   - overdue:            "< 0s"
const countdownLabel = (remainingMs, intervalMs) => {
    if (remainingMs <= 0) {
        return '< 0s';
    }
    if (intervalMs < 10 * 1000) {
        return `in ${Math.ceil(remainingMs / 1000)}s`;
    }
    if (remainingMs >= 60 * 1000) {
        return `in ${Math.ceil(remainingMs / 60000)} min`;
    }
    return `in ${Math.ceil(remainingMs / 5000) * 5}s`;
};

// Shows the detected sample rate and a live countdown to the next expected sample
// (last sample + detected rate). No progress bar — real gateways don't report on an exact
// schedule, so the countdown is just an estimate that resets when a new sample arrives.
const GatewayLiveCadence = ({ samples }) => {
    const interval = medianInterval(samples);
    const latest = samples.length ? samples[samples.length - 1] : null;
    const lastTime = latest ? new Date(latest.timestamp ?? latest.time).getTime() : null;
    const nextExpected = interval && lastTime ? lastTime + interval : null;

    const [text, setText] = useState('');

    // Read live values via refs so a single polling timer always uses the latest sample —
    // no per-sample timer churn, and no chance of a stale timer firing an old countdown.
    const nextRef = useRef(nextExpected);
    const intervalRef = useRef(interval);
    nextRef.current = nextExpected;
    intervalRef.current = interval;

    useEffect(() => {
        // One timer for the component's lifetime. Polls 4x/sec but only re-renders when the
        // label actually changes (React bails on identical state), so it's cheap.
        const id = setInterval(() => {
            if (!nextRef.current) {
                setText('');
                return;
            }
            const label = countdownLabel(nextRef.current - Date.now(), intervalRef.current);
            setText(prev => (prev === label ? prev : label));
        }, 250);
        return () => clearInterval(id);
    }, []);

    if (!interval) {
        return null;
    }

    return (
        <div className="gw-cadence">
            <span className="gw-cadence__item">
                <span className="gw-cadence__label">Sample rate</span>
                <span className="gw-cadence__value">{formatRate(interval)}</span>
            </span>
            <span className="gw-cadence__item">
                <span className="gw-cadence__label">Next expected</span>
                <span className="gw-cadence__value">{text}</span>
            </span>
        </div>
    );
};

export default GatewayLiveCadence;
